import React, {useContext, useState} from "react";
import {Box, Button, Fade, IconButton, Modal, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, Cancel01Icon, PencilEdit01Icon} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {SignInContext} from "../../context/SignInContext";
import {ProjectRoles} from "../../context/AppContext";
import {ApiKeyRepository} from "../../repositories/ApiKeyRepository";
import {BackendConfigurationRepository} from "../../repositories/BackendConfigurationRepository";
import {useApiKeys} from "../../pages/api-keys/useApiKeys";
import {ApiKeysTable} from "../../pages/api-keys/components/api-keys-table";
import {BackendConfigurationDialog} from "../../pages/backend-configurations/components/backend-configuration-dialog";
import {SERVICE_FIELDS} from "../../pages/backend-configurations/serviceFields";
import {modalPaperSx} from "../../components/modals/formFieldShared";
import {useRegistryProject} from "../../pages/registry-project/RegistryProjectContext";

const smallButtonSx = {
  height: 32, px: 1.5, borderRadius: "10px", textTransform: "none", fontSize: 13, fontWeight: 600, whiteSpace: "nowrap",
  color: "#374151", bgcolor: "#FAFAFD", "&:hover": {bgcolor: "#ECECF3"},
};

const ModalShell = ({open, onClose, title, subtitle, action, width, children}) => (
  <Modal open={open} onClose={onClose}>
    <Fade in={open}>
      <Box sx={modalPaperSx(width)}>
        <Stack direction={"row"} justifyContent={"space-between"} alignItems={"flex-start"} gap={1.5} sx={{p: 3, pb: 2}}>
          <Box sx={{minWidth: 0}}>
            <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>{title}</Typography>
            {subtitle && <Typography sx={{fontSize: 13, color: "#8A8AA0", mt: 0.25}}>{subtitle}</Typography>}
          </Box>
          <Stack direction={"row"} alignItems={"center"} gap={1} sx={{flexShrink: 0}}>
            {action}
            <IconButton size={"small"} onClick={onClose} aria-label={"Close"} sx={{color: "#8A8AA0"}}>
              <HugeiconsIcon icon={Cancel01Icon} size={18}/>
            </IconButton>
          </Stack>
        </Stack>
        <Box sx={{overflow: "auto", pb: 1}}>{children}</Box>
      </Box>
    </Fade>
  </Modal>
);

// Quick view of the project's backend configuration. Editing changes the shared configuration, so it is limited to
// the users who manage backend configurations (system admins and managers).
export const ProjectBackendConfigurationModal = ({open, onClose}) => {
  const {currentUser, organizations} = useContext(SignInContext);
  const {project, refreshProject} = useRegistryProject();
  const [editOpen, setEditOpen] = useState(false);
  const configuration = project?.backendConfiguration;
  const canEdit = Boolean(configuration?.id)
    && currentUser?.roles?.some(role => role === "admin" || role === "manager");

  const handleSave = async (data) => {
    try {
      await BackendConfigurationRepository.edit(configuration.id, data);
      await refreshProject();
      toast.success("Backend configuration saved successfully");
      return true;
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Failed to save backend configuration");
      return false;
    }
  };

  const services = SERVICE_FIELDS.filter(field => configuration?.[field.name]);

  return (
    <>
      <BackendConfigurationDialog
        open={editOpen}
        selectedConfiguration={configuration}
        handleClose={() => setEditOpen(false)}
        organizations={organizations}
        onSubmit={handleSave}
      />
      <ModalShell
        open={open && !editOpen} onClose={onClose} width={520}
        title={"Backend Configuration"}
        subtitle={configuration?.name || "This project has no backend configuration"}
        action={canEdit && (
          <Button
            onClick={() => setEditOpen(true)} sx={smallButtonSx}
            startIcon={<HugeiconsIcon icon={PencilEdit01Icon} size={15}/>}
          >
            Edit
          </Button>
        )}
      >
        <Stack gap={1.25} sx={{px: 3, pb: 2}}>
          {services.length === 0 && (
            <Typography sx={{fontSize: 13, color: "#8A8AA0", py: 3, textAlign: "center"}}>
              {configuration ? "No service URLs are set" : "Choose one in the project's General settings"}
            </Typography>
          )}
          {services.map(field => (
            <Box key={field.name} sx={{p: 1.5, borderRadius: "12px", bgcolor: "#FAFAFD"}}>
              <Typography sx={{fontSize: 11, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase", color: "#8A8AA0"}}>
                {field.label}
              </Typography>
              <Typography sx={{fontFamily: "monospace", fontSize: 12.5, color: "#23264D", wordBreak: "break-all", mt: 0.25}}>
                {configuration[field.name]}
              </Typography>
            </Box>
          ))}
          {canEdit && (
            <Typography sx={{fontSize: 12, color: "#8A8AA0"}}>
              This configuration is shared: changes apply to every project that uses it.
            </Typography>
          )}
        </Stack>
      </ModalShell>
    </>
  );
};

// Quick view of the API keys of the project's organization (keys belong to organizations, not to projects).
// Creating, changing and revoking keys needs project manage access.
export const ProjectApiKeysModal = ({open, onClose, role}) => {
  const {currentUser} = useContext(SignInContext);
  const {project} = useRegistryProject();
  const organizationId = project?.organizationId ?? null;
  const canEdit = Boolean(organizationId) && (
    role === ProjectRoles.ADMIN || role === ProjectRoles.MANAGER || currentUser?.roles?.includes("admin")
  );

  const {apiKeys, openCreate, dialogs, tableProps} = useApiKeys({
    fetchKeys: async () => {
      if (!organizationId) return [];
      const response = await ApiKeyRepository.getMyKeys();
      return (response.data || []).filter(apiKey => apiKey.organizationId === organizationId);
    },
    deps: [organizationId],
    organizationId,
  });

  return (
    <>
      {dialogs}
      <ModalShell
        open={open} onClose={onClose} width={760}
        title={"API Keys"}
        subtitle={project?.organizationName ? `Keys of ${project.organizationName}` : "This project has no organization"}
        action={canEdit && (
          <Button onClick={openCreate} sx={smallButtonSx} startIcon={<HugeiconsIcon icon={Add01Icon} size={15}/>}>
            Create Key
          </Button>
        )}
      >
        <Box sx={{mx: 3, mb: 2, borderRadius: "14px", overflow: "hidden", bgcolor: "#fff"}}>
          <ApiKeysTable
            {...tableProps}
            apiKeys={apiKeys}
            hideOrganization
            readOnly={!canEdit}
            emptyText={organizationId ? "No API keys for this organization yet" : "API keys belong to an organization"}
          />
        </Box>
      </ModalShell>
    </>
  );
};
